import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Button, ButtonVariantOption } from "@/components/Button";

const DEFAULT_MESSAGE =
  "ページを離れると最初からやり直しになります。ページを離れますか？";

/**
 * クリックすると別アプリが開き、ページが残るスキーム。
 * javascript: のように遷移しないものを含めると、抑止フラグを戻す機会がなくなる
 */
const EXTERNAL_APP_PROTOCOLS = new Set(["mailto:", "tel:"]);

const opensExternalApp = (href: string): boolean => {
  try {
    const target = new URL(href, window.location.href);
    return EXTERNAL_APP_PROTOCOLS.has(target.protocol);
  } catch {
    return false;
  }
};

/**
 * 遷移すると現在のページが破棄されるURLか。
 * mailto:/tel: はアプリを開くだけ、#以降だけが違うURLはページ内移動で、いずれもページが残る
 */
const unloadsPage = (href: string): boolean => {
  let target: URL;
  try {
    target = new URL(href, window.location.href);
  } catch {
    return false;
  }
  if (target.protocol !== "http:" && target.protocol !== "https:") {
    return false;
  }
  return target.href.split("#")[0] !== window.location.href.split("#")[0];
};

/**
 * ページ離脱前に確認ダイアログを出す共通フック。
 * 離脱するとやり直せない処理（時間のかかるローディング、消費済みの受け渡しデータ等）を
 * 抱えた画面で使う。戻り値の dialog を画面の JSX に含めること。
 * 有効な間だけ以下の2経路を監視する。
 * - リロード・タブ閉じ・戻るボタン・外部サイトへの遷移: beforeunload イベントで
 *   ブラウザ標準の確認が出る（文言のカスタマイズは不可）。
 *   ただし確認は、ページ内でクリックやキー入力が一度でも行われている場合に限り表示される
 *   （更新・戻るボタンを押す操作自体はこれに含まれない）。
 *   戻るボタンは別ドキュメントへの遷移になる場合のみ確認され、
 *   SPA 内の履歴を戻る操作（同一ドキュメント内の遷移）は検知できない
 * - リンクのクリック: いったん遷移を止めて確認ダイアログを出し、了承されたらリンク先へ遷移する。
 *   mailto:・ページ内リンク・download 付きリンクは現在のページを離れないため対象外。
 *   このうち mailto: と download 付きリンクは遷移の開始で beforeunload が発火し
 *   ブラウザ標準の確認だけが出てしまうため抑止する（抑止は beforeunload での消費と、
 *   発火しないブラウザ向けにページへ戻った時点でも戻す）。
 *   `<a>` を経由しない navigate() 直呼びの遷移は検知できない
 * @param enabled 監視を有効にするか。false の間は何もしない
 * @param message 確認ダイアログの文言。未指定なら汎用の文言を使う
 */
export const useLeaveConfirmation = (
  enabled: boolean,
  message: string = DEFAULT_MESSAGE
): { dialog: React.ReactNode } => {
  const navigate = useNavigate();
  const id = useId();
  // 了承時に遷移するリンク先。null ならダイアログを閉じている
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  // 外部URLへの了承済み遷移で beforeunload の標準確認を二重に出さないためのフラグ
  const leaveApprovedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    leaveApprovedRef.current = false;

    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      // ダイアログで了承済みの離脱では、ブラウザ標準の確認を重ねて出さない。
      // 一度だけ消費してすぐ戻すことで、遷移がキャンセルされた場合の再訪問時には抑止が残らない
      if (leaveApprovedRef.current) {
        leaveApprovedRef.current = false;
        return;
      }
      e.preventDefault();
      e.returnValue = "";
    };

    const handleClick = (e: MouseEvent): void => {
      // 修飾キー押下や別タブで開くリンクは現在のページを離れない
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const anchor =
        e.target instanceof Element ? e.target.closest("a[href]") : null;
      const href = anchor?.getAttribute("href");
      if (
        anchor == null ||
        href == null ||
        anchor.getAttribute("target") === "_blank"
      ) {
        return;
      }
      // ダウンロードと別アプリ起動はページが残るため確認は不要。ただし遷移の開始で
      // beforeunload が発火してブラウザ標準の確認だけが出るため、抑止フラグを立てる
      if (anchor.hasAttribute("download") || opensExternalApp(href)) {
        leaveApprovedRef.current = true;
        return;
      }
      // ページ内リンクは遷移自体が起きず、標準確認も出ないため何もしない
      if (!unloadsPage(href)) {
        return;
      }
      e.preventDefault();
      setPendingHref((prev) => prev ?? href);
    };

    // mailto: では beforeunload を発火させないブラウザがあり、抑止フラグを消費できない。
    // ページに戻ってきた時点で戻すことで抑止を残さない。復帰の通知は環境によって
    // focus・pageshow・visibilitychange に分かれるため、いずれでも戻す。
    // どれも beforeunload より後に発火するため、了承済みの離脱を邪魔しない
    const resetLeaveApproved = (): void => {
      leaveApprovedRef.current = false;
    };
    const handleVisibilityChange = (): void => {
      // hidden 側は離脱の途中でも発火するため、戻ってきたときだけ扱う
      if (document.visibilityState === "visible") {
        resetLeaveApproved();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("focus", resetLeaveApproved);
    window.addEventListener("pageshow", resetLeaveApproved);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("click", handleClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("focus", resetLeaveApproved);
      window.removeEventListener("pageshow", resetLeaveApproved);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("click", handleClick, true);
      setPendingHref(null);
    };
  }, [enabled]);

  const handleCancel = useCallback(() => {
    setPendingHref(null);
  }, []);
  const handleLeave = useCallback(() => {
    const href = pendingHref;
    setPendingHref(null);
    if (href == null) {
      return;
    }
    const url = new URL(href, window.location.href);
    if (url.origin === window.location.origin) {
      navigate(url.pathname + url.search + url.hash);
      return;
    }
    // 外部URLは react-router のパスとして解釈できないため、ブラウザの遷移で開く。
    // 実際の離脱に伴う beforeunload は assign() 呼び出しの後続タスクで発火するため、
    // ここで同期的にフラグを戻すと抑止が間に合わない。遷移先がダウンロードで
    // ページが残る場合も beforeunload は発火するため、戻す役目は同ハンドラに任せる
    leaveApprovedRef.current = true;
    window.location.assign(href);
  }, [navigate, pendingHref]);

  // 利用画面は再レンダーが頻繁なため、開閉状態と文言が変わらない限り再生成しない
  const open = pendingHref != null;
  const titleId = `leave-confirmation-dialog-title-${id}`;
  const descriptionId = `leave-confirmation-dialog-description-${id}`;
  const dialog = useMemo(
    () => (
      <Dialog
        open={open}
        onClose={handleCancel}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <DialogTitle id={titleId}>確認</DialogTitle>
        <DialogContent>
          <DialogContentText id={descriptionId}>{message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
          <Button
            label="キャンセル"
            onClick={handleCancel}
            variant={ButtonVariantOption.Outlined}
          />
          <Button
            label="ページを離れる"
            onClick={handleLeave}
            variant={ButtonVariantOption.Contained}
          />
        </DialogActions>
      </Dialog>
    ),
    [open, message, handleCancel, handleLeave, titleId, descriptionId]
  );

  return { dialog };
};
